import type { LobbyInfo } from '../../api/types';
import { isSeriesFormat, type SeriesFormat } from '../../domain/series';

/**
 * The API runs a series on one bot and creates a new lobby for every game, but
 * it exposes no "list series" endpoint: the lobby list only carries Valve's
 * series fields, which cannot tell a bo2 from a bo3 and say nothing between
 * games. So the client remembers the series it started, keyed by bot, and
 * reads the live state off whatever lobby that bot currently hosts.
 *
 * This is a per-browser memory, like players and teams: another browser
 * sees the lobby but not the format or the team names.
 */
export type TrackedSeries = {
  botId: string;
  /** The lobby of game 1; later games get new lobby IDs. */
  firstLobbyId: string;
  name: string;
  format: SeriesFormat;
  maxGames: number;
  radiantTeam: string;
  direTeam: string;
  playersPerTeam: number;
  startedAt: number;
  /** Last time the lobby list showed a lobby on this bot. */
  lastSeenAt: number;
};

export const TRACKED_SERIES_KEY = 'trackedSeries';

/** A series older than this is forgotten: no series runs that long. */
export const TRACKED_SERIES_MAX_AGE_MS = 12 * 60 * 60 * 1000;

/**
 * Between games the bot has no lobby for well under a minute; past this
 * grace the series has most likely finished or been cancelled.
 */
export const BETWEEN_GAMES_GRACE_MS = 3 * 60 * 1000;

/** lastSeenAt is only rewritten this often, to keep the storage writes rare. */
export const SEEN_WRITE_INTERVAL_MS = 30 * 1000;

function isTracked(value: unknown): value is TrackedSeries {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.botId === 'string' &&
    record.botId !== '' &&
    isSeriesFormat(record.format) &&
    typeof record.startedAt === 'number'
  );
}

export function parseTrackedSeries(raw: string | null): TrackedSeries[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isTracked).map((entry) => ({
      ...entry,
      firstLobbyId: String(entry.firstLobbyId ?? ''),
      name: String(entry.name ?? ''),
      maxGames: Number(entry.maxGames) || 1,
      radiantTeam: String(entry.radiantTeam ?? ''),
      direTeam: String(entry.direTeam ?? ''),
      playersPerTeam: Number(entry.playersPerTeam) || 0,
      lastSeenAt: Number(entry.lastSeenAt) || entry.startedAt,
    }));
  } catch {
    return [];
  }
}

export function pruneTrackedSeries(list: TrackedSeries[], now: number): TrackedSeries[] {
  return list.filter((entry) => now - entry.startedAt < TRACKED_SERIES_MAX_AGE_MS);
}

/** A bot runs one series at a time, so a new series on a bot replaces the old record. */
export function rememberSeries(list: TrackedSeries[], entry: TrackedSeries): TrackedSeries[] {
  return [entry, ...list.filter((existing) => existing.botId !== entry.botId)];
}

export function forgetSeries(list: TrackedSeries[], botId: string): TrackedSeries[] {
  return list.filter((entry) => entry.botId !== botId);
}

/**
 * Whether a lobby is one of the series' games: same bot, same name (every
 * game's lobby carries the series name), and either the game 1 lobby itself or
 * a lobby with Valve's series type set (game 2 and later). A plain lobby
 * opened later on the same bot, or a bo1 that is not game 1, does not match,
 * so a record that outlived its series cannot dress up another lobby.
 */
export function lobbyBelongsTo(entry: TrackedSeries, lobby: LobbyInfo): boolean {
  if (lobby.bot_id !== entry.botId) return false;
  if ((lobby.game_name ?? '') !== entry.name) return false;
  return String(lobby.lobby_id) === entry.firstLobbyId || (lobby.series_type ?? 0) > 0;
}

export function seriesLobbyOf(entry: TrackedSeries, lobbies: LobbyInfo[]): LobbyInfo | undefined {
  return lobbies.find((lobby) => lobbyBelongsTo(entry, lobby));
}

export function trackedSeriesFor(lobby: LobbyInfo, list: TrackedSeries[]): TrackedSeries | null {
  return list.find((entry) => lobbyBelongsTo(entry, lobby)) ?? null;
}

/**
 * Refreshes lastSeenAt for every tracked series whose lobby is in the list.
 * Returns the same array when nothing needs writing, so callers can skip the
 * state update and the storage write.
 */
export function markSeen(
  list: TrackedSeries[],
  lobbies: LobbyInfo[],
  now: number,
  minInterval = SEEN_WRITE_INTERVAL_MS,
): TrackedSeries[] {
  let changed = false;
  const next = list.map((entry) => {
    if (!seriesLobbyOf(entry, lobbies)) return entry;
    if (now - entry.lastSeenAt < minInterval) return entry;
    changed = true;
    return { ...entry, lastSeenAt: now };
  });
  return changed ? next : list;
}

export type TrackedSeriesStatus =
  | { kind: 'live'; lobby: LobbyInfo }
  | { kind: 'between'; sinceMs: number }
  | { kind: 'gone'; sinceMs: number };

/**
 * live: one of the series' lobbies is up right now. between: none, but there
 * was one moments ago (the bot is creating the next game's lobby, or the
 * series just ended). gone: no lobby for longer than a between-games break.
 */
export function trackedSeriesStatus(
  entry: TrackedSeries,
  lobbies: LobbyInfo[],
  now: number,
): TrackedSeriesStatus {
  const lobby = seriesLobbyOf(entry, lobbies);
  if (lobby) return { kind: 'live', lobby };
  const sinceMs = Math.max(0, now - entry.lastSeenAt);
  return sinceMs < BETWEEN_GAMES_GRACE_MS ? { kind: 'between', sinceMs } : { kind: 'gone', sinceMs };
}
